/**
 * @fileoverview Repetition tiers a habit is celebrated for crossing.
 * @module lib/domain/milestones
 *
 * @description
 * Fixed regardless of any habit's own goal: a habit's `goalReps` (default 66)
 * is the target the user picked; these tiers are the shared "well done"
 * checkpoints every habit is measured against as reps pile up over time.
 */

/** Repetition tiers, ascending. Reaching one triggers a celebratory toast. */
export const MILESTONES = [
	1, 15, 33, 66, 100, 200, 500, 1000, 2500, 5000, 10000,
] as const

/** One of the fixed milestone tiers. */
export type Milestone = (typeof MILESTONES)[number]

/** Default goal, in repetitions, offered when a habit is created. */
export const DEFAULT_GOAL_REPS: Milestone = 66

/**
 * Formats a milestone with a thin space as the thousands separator.
 *
 * @param value - The tier to format, e.g. `10000`.
 * @returns A French-style figure, e.g. `"10 000"`.
 */
export function formatMilestone(value: number): string {
	return new Intl.NumberFormat("fr-FR").format(value)
}

/**
 * Every tier at or below `totalReps`.
 *
 * @param totalReps - Repetitions logged so far.
 * @returns Tiers reached, ascending.
 */
export function achievedMilestones(totalReps: number): Milestone[] {
	return MILESTONES.filter(tier => tier <= totalReps)
}

/**
 * Where a habit stands against the fixed tiers.
 *
 * @param totalReps - Repetitions logged so far.
 * @returns Tiers reached, the next tier to reach, and progress toward it.
 *
 * @example
 * computeMilestoneProgress(40)
 * // => { achieved: [1, 15, 33], next: 66, ratioToNext: 0.606... }
 */
export function computeMilestoneProgress(totalReps: number) {
	const achieved = achievedMilestones(totalReps)
	const next = MILESTONES.find(tier => tier > totalReps) ?? null
	const previous = achieved.at(-1) ?? 0

	const ratioToNext =
		next === null
			? 1
			: Math.min(
					1,
					Math.max(0, (totalReps - previous) / (next - previous))
				)

	return { achieved, next, ratioToNext }
}

/**
 * Every tier crossed strictly between two totals, ascending.
 *
 * @description
 * Used right after logging reps to decide which celebratory toasts to fire —
 * a single log can cross more than one tier (e.g. 0 -> 20 crosses 1 and 15).
 *
 * @param before - Total before the log.
 * @param after - Total after the log.
 * @returns Tiers now reached that were not reached before.
 */
export function crossedMilestones(before: number, after: number): Milestone[] {
	if (after <= before) return []
	return MILESTONES.filter(tier => tier > before && tier <= after)
}
